import { EventEmitter, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Component, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, of } from 'rxjs';
import { HasChanges } from 'src/app/services/guards/unsaved-changes.guard';
import {
  DxButtonModule,
  DxSelectBoxComponent,
  DxSelectBoxModule,
  DxTagBoxModule,
  DxTextBoxModule,
  DxToastModule,
  DxValidationGroupModule,
  DxValidationGroupComponent,
  DxValidatorModule,
} from 'devextreme-angular';
import { ConfigService } from 'src/app/services/config.service';
import { ContactService } from 'src/app/services/contact.service';
import { GridConnectionService } from 'src/app/services/grid-connection.service';
import { AppGlobals } from '../../app.globals';
import { Router } from '@angular/router';

import { ClickEvent } from 'devextreme/ui/button';
import { GpdButtonComponent } from '../gpd-button/gpd-button.component';
import { GpdPillComponent } from '../gpd-pill/gpd-pill.component';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { FormFooterComponent } from '../form-footer/form-footer.component';
import { ValidationToastComponent } from '../validation-toast/validation-toast.component';

import { SearchableDropdownDirective } from '../../directives/searchable-dropdown.directive';
@Component({
    selector: 'app-contacts-add-company',
    imports: [
        CommonModule,
        DxButtonModule,
        DxTagBoxModule,
        DxToastModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxSelectBoxModule,
        DxValidationGroupModule,
        GpdButtonComponent,
        GpdPillComponent,
        SvgIconComponent,
        FormFooterComponent,
        ValidationToastComponent,
        SearchableDropdownDirective,
    ],
    templateUrl: './contacts-add-company.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./contacts-add-company.component.css']
})
export class ContactsAddCompanyComponent implements OnInit, HasChanges {
  type: any = '';
  message: any = '';
  toastVisible: boolean = false;

  @ViewChild('companyValidationGroup') companyValidationGroup: DxValidationGroupComponent;
  @ViewChild('validationToast') validationToast: ValidationToastComponent;

  // Same unsaved-changes guard as the contract form (see hasChangesGuard): baseline
  // snapshot taken once ngOnInit's prefill is done, compared against on every navigation
  // attempt away from this page.
  hasChanges: boolean = false;
  isSavingChanges: boolean = false;
  private initialFormData: any;

  formData = {
    id: null,
    name: null,
    tags: null,
    address: null,
    addition: null,
    city: null,
    zipcode: null,
    country: null,
    countryId: 2, // Germany - default for the majority of domestic companies
    stateId: null,
    phone: null,
  };

  @Input('returnRouteSubmit') returnRouteSubmit: string;
  @Input('returnRouteAbort') returnRouteAbort: string;
  @Input() prePopulatePartner: boolean;
  countries: string[] = [$localize`:@@germany:Germany`, $localize`:@@belgium:Belgium`, $localize`:@@france:France`];

  contactTags: any;
  companyData: any;

  @Output() onSubmit = new EventEmitter<any>();
  @Output() onAbort = new EventEmitter<any>();

  @Output() onAddPartner = new EventEmitter<any>();

  countriesByLanguageList: any = [];
  statesByLanguageList: any = [];

  constructor(
    private configService: ConfigService,
    private contactService: ContactService,
    private gridConnectionService: GridConnectionService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.prePopulatePartner === true) {
      this.formData.tags = [100];
    }
    /*     this.getStatesByLanguage(2); */

    // Snapshot taken synchronously, before any await: loadContactTags() below is a real
    // network call the very first time it's called in a session (uncached) - awaiting it
    // here delayed the baseline snapshot long enough that a fast back-button press right
    // after landing on the page raced ahead of it, leaving initialFormData unset and the
    // unsaved-changes guard silently reporting no changes.
    this.initialFormData = JSON.parse(JSON.stringify(this.formData));

    this.loadAsyncData();
  }

  private async loadAsyncData(): Promise<void> {
    this.contactTags = await this.configService.loadContactTags();
    this.getCountriesByLanguage();
  }

  checkHasChanges(): void {
    if (!this.initialFormData) {
      this.hasChanges = false;
      return;
    }
    this.hasChanges = JSON.stringify(this.formData) !== JSON.stringify(this.initialFormData);
  }

  saveUnsavedChanges(): Observable<boolean> {
    this.handleSubmitButton(null);
    return of(false);
  }

  // Tag id -> title, for rendering the selected-tags pills above the dropdown.
  getTag(id: any): string {
    return this.contactTags?.find((tag: any) => tag.id == id)?.title ?? '';
  }

  // The "x" on a pill removes just that tag — the dropdown below stays in sync since
  // both render from the same formData.tags array.
  removeTag(id: any): void {
    this.formData.tags = (this.formData.tags ?? []).filter((t: any) => t != id);
  }

  handleSubmitButton(event: any): void {
    const result = this.companyValidationGroup.instance.validate();
    if (result.isValid) {
      this.handleSubmit(event);
    } else {
      this.validationToast.showToast($localize`Please fill in all fields before saving`);
    }
  }

  handleAbort(): void {
    this.onAbort.emit({ returnTo: this.returnRouteAbort });
  }

  handleSubmit(e: any): void {
    if (this.formData.name != '' && this.formData.name != null) {
      this.hasChanges = false;
      this.isSavingChanges = true;

      this.contactService.addCompany(this.formData).then(
        result => {
          if (result.hasError === true) {
            this.isSavingChanges = false;
            AppGlobals.ShowErrorMessage(result.feedback);
          } else {
            this.onSubmit.emit({
              data: result.companyItems[0],
              returnTo: this.returnRouteSubmit,
            });

            this.onAddPartner.emit({
              id: result.companyItems[0].id,
              name: this.formData.name,
            });
            this.saveToast({ type: true, feedback: $localize`:@@saved:Saved` });
          }
        },
        (error: any) => {
          this.isSavingChanges = false;
          AppGlobals.ShowErrorMessage(error);
        }
      );
    }
  }

  saveToast(e: any): void {
    const type = e.type ? 'success' : 'error';
    if ((this.type = 'success')) {
      this.message = e.feedback;
    } else {
      AppGlobals.ShowErrorMessage($localize`:@@errorTryAgain:ERROR: There was an error, please try again.`);
    }
    this.type = type;
    this.toastVisible = true;
  }

  getCountriesByLanguage(): void {
    this.gridConnectionService.getCountries({}).then(
      result => {
        if (result.hasError === true) AppGlobals.ShowErrorMessage(result.feedback);
        else {
          this.countriesByLanguageList = result.countryList;
        }
      },
      (error: any) => {
        AppGlobals.ShowErrorMessage(error);
      }
    );
  }

  /*   getStatesByLanguage(countryID: any): void {
      let selectedCountry = { "CountryId": countryID, }
  
      this.gridConnectionService.getStates(selectedCountry).then((result) => {
        if (result.hasError === true) AppGlobals.ShowErrorMessage(result.feedback);
        else {
          this.statesByLanguageList = result.stateList;
        }
      },
        (error: any) => {
          AppGlobals.ShowErrorMessage(error);
        });
  
    } */

  handleCountryChange(e: any): void {}

  goTo(link) {
    this.router.navigate([link]);
  }
}
